/* @jsx h */
import { defineComponent, h, ref, computed, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import { Dropdown, useLocalization } from "@lemmonhq/uikit";
import { AppSubpage, AppSubpageContent } from "@app/vue/components/AppSubpage";
import { AppSubpageHeader } from "@app/vue/components/AppSubpageHeader";
import { useTrpc } from "@app/vue/providers/TrpcProvider";
import { WalletDesignCard, WalletDesignEditor } from "./components";
import { createDraftDesign, isUnsaved } from "./document";
import {
  KIND_META,
  PASS_KINDS,
  type WalletPassDesign,
  type WalletPassKind,
} from "./types";

type KindFilter = "all" | WalletPassKind;

export type LoyaltyWalletDesignsPageProps = {
  scope?: "shop" | "group";
  canManage?: boolean;
};

export const LoyaltyWalletDesignsPage =
  defineComponent<LoyaltyWalletDesignsPageProps>({
    name: "LoyaltyWalletDesignsPage",
    props: ["scope", "canManage"],
    setup(props) {
      const { t } = useLocalization();
      const trpc = useTrpc();

      const designs = ref<WalletPassDesign[]>([]);
      const loading = ref<boolean | Error>(true);
      const saving = ref(false);
      const actionError = ref<string | null>(null);

      const kindFilter = ref<KindFilter>("all");
      const editing = ref<WalletPassDesign | null>(null);
      const route = useRoute();
      const router = useRouter();

      /**
       * Program editors link here with ?design=<id> ("Edit design") or
       * ?new=<kind> ("New design"); open the editor, then drop the query so a
       * reload does not reopen it.
       */
      const openFromQuery = () => {
        const designId = route.query.design;
        const newKind = route.query.new;
        if (typeof designId === "string") {
          editing.value = designs.value.find((d) => d.id === designId) ?? null;
        } else if (
          typeof newKind === "string" &&
          (PASS_KINDS as string[]).includes(newKind)
        ) {
          handleCreate(newKind as WalletPassKind);
        } else {
          return;
        }
        void router.replace({ query: {} });
      };

      const loadDesigns = async () => {
        loading.value = true;
        try {
          designs.value =
            props.scope === "group"
              ? await trpc.walletDesigns.groupList.query({})
              : await trpc.walletDesigns.list.query({});
          loading.value = false;
        } catch (e) {
          loading.value = e instanceof Error ? e : new Error(String(e));
        }
      };

      onMounted(async () => {
        await loadDesigns();
        openFromQuery();
      });

      /** Mutations report failures inline rather than leaving the list stale. */
      const run = async (fn: () => Promise<unknown>) => {
        saving.value = true;
        actionError.value = null;
        try {
          await fn();
        } catch (e) {
          actionError.value = e instanceof Error ? e.message : String(e);
        } finally {
          saving.value = false;
        }
      };

      const visible = computed(() =>
        designs.value.filter(
          (d) => kindFilter.value === "all" || d.kind === kindFilter.value,
        ),
      );

      const countFor = (kind: WalletPassKind) =>
        designs.value.filter((d) => d.kind === kind).length;

      /** A draft has no id until this point — Save is what creates it. */
      const handleSave = (updated: WalletPassDesign) =>
        run(async () => {
          if (isUnsaved(updated)) {
            const input = {
              kind: updated.kind,
              name: updated.name,
              document: updated.document,
            };
            const created =
              props.scope === "group"
                ? await trpc.walletDesigns.groupCreate.mutate(input)
                : await trpc.walletDesigns.create.mutate(input);
            designs.value = [...designs.value, created];
          } else {
            const input = {
              id: updated.id,
              name: updated.name,
              document: updated.document,
            };
            const saved =
              props.scope === "group"
                ? await trpc.walletDesigns.groupUpdate.mutate(input)
                : await trpc.walletDesigns.update.mutate(input);
            designs.value = designs.value.map((d) =>
              d.id === saved.id ? saved : d,
            );
          }
          editing.value = null;
        });

      const handleDuplicate = (design: WalletPassDesign) =>
        run(async () => {
          const copy =
            props.scope === "group"
              ? await trpc.walletDesigns.groupDuplicate.mutate({
                  id: design.id,
                })
              : await trpc.walletDesigns.duplicate.mutate({ id: design.id });
          designs.value = [...designs.value, copy];
        });

      const handleDelete = (design: WalletPassDesign) =>
        run(async () => {
          if (props.scope === "group") {
            await trpc.walletDesigns.groupDelete.mutate({ id: design.id });
          } else {
            await trpc.walletDesigns.delete.mutate({ id: design.id });
          }
          designs.value = designs.value.filter((d) => d.id !== design.id);
        });

      // Opens the editor on a local draft; nothing is persisted until Save, so
      // cancelling leaves no row behind.
      const handleCreate = (kind: WalletPassKind) => {
        actionError.value = null;
        editing.value = createDraftDesign(
          kind,
          `${KIND_META[kind].label} design ${countFor(kind) + 1}`,
        );
      };

      const renderKindFilter = () => (
        <div class="flex gap-2 flex-wrap items-center">
          <button
            class={`btn btn-sm ${kindFilter.value === "all" ? "btn-primary" : "btn-ghost"}`}
            onClick={() => (kindFilter.value = "all")}
          >
            All
          </button>
          {PASS_KINDS.map((kind) => (
            <button
              key={kind}
              class={`btn btn-sm gap-1.5 ${kindFilter.value === kind ? "btn-primary" : "btn-ghost"}`}
              onClick={() => (kindFilter.value = kind)}
            >
              <span class="material-symbols-outlined text-sm">
                {KIND_META[kind].icon}
              </span>
              {KIND_META[kind].label}
              <span class="badge badge-ghost badge-xs">{countFor(kind)}</span>
            </button>
          ))}
        </div>
      );

      const renderNewMenu = () => (
        <Dropdown
          placement="bottom-end"
          offset={8}
          closeOnContentClick
          contentClass="menu menu-sm w-56 rounded-xl border border-base-content/10 bg-base-300 p-1.5 shadow-xl"
        >
          {{
            trigger: () => (
              <button
                class="btn btn-sm btn-primary gap-1.5"
                disabled={saving.value || props.canManage === false}
              >
                <span class="material-symbols-outlined text-sm">add</span>
                New design
              </button>
            ),
            default: () =>
              PASS_KINDS.map((kind) => (
                <li key={kind}>
                  <a onClick={() => handleCreate(kind)}>
                    <span class="material-symbols-outlined text-base">
                      {KIND_META[kind].icon}
                    </span>
                    {KIND_META[kind].label}
                  </a>
                </li>
              )),
          }}
        </Dropdown>
      );

      const renderBody = () => {
        if (loading.value === true) {
          return (
            <div class="flex justify-center py-16">
              <span class="loading loading-spinner loading-lg" />
            </div>
          );
        }

        if (loading.value instanceof Error) {
          return (
            <div class="alert alert-error m-4">
              <span>Failed to load designs: {loading.value.message}</span>
              <button class="btn btn-sm" onClick={loadDesigns}>
                Retry
              </button>
            </div>
          );
        }

        if (visible.value.length === 0) {
          return (
            <div class="flex flex-col items-center justify-center py-20 gap-3 text-base-content/40">
              <span class="material-symbols-outlined text-6xl">wallet</span>
              <p class="text-sm">{t("promotions.wallet.empty")}</p>
            </div>
          );
        }

        return (
          <div class="p-4 grid gap-4 grid-cols-1 md:grid-cols-2 xl:grid-cols-3">
            {visible.value.map((design) => (
              <WalletDesignCard
                key={design.id}
                design={design}
                canManage={props.canManage}
                onEdit={(d) => (editing.value = d)}
                onDuplicate={handleDuplicate}
                onDelete={handleDelete}
              />
            ))}
          </div>
        );
      };

      return () => (
        <AppSubpage>
          <AppSubpageHeader
            title={t("promotions.wallet.page_title")}
            icon="wallet"
            description={t("promotions.wallet.page_description")}
            v-slots={{
              actions: () => renderNewMenu(),
              bottom: () => <div class="pb-3">{renderKindFilter()}</div>,
            }}
          />

          <AppSubpageContent>
            {actionError.value && (
              <div class="alert alert-error mx-4 mt-4">
                <span>{actionError.value}</span>
                <button
                  class="btn btn-sm btn-ghost"
                  onClick={() => (actionError.value = null)}
                >
                  Dismiss
                </button>
              </div>
            )}
            {renderBody()}
          </AppSubpageContent>

          <WalletDesignEditor
            design={editing.value}
            saving={saving.value}
            scope={props.scope}
            onClose={() => (editing.value = null)}
            onSave={handleSave}
            onSendTest={async (design: WalletPassDesign) => {
              const result =
                props.scope === "group"
                  ? await trpc.walletDesigns.groupSendTest.mutate({
                      id: design.id,
                    })
                  : await trpc.walletDesigns.sendTest.mutate({ id: design.id });
              return result.to;
            }}
          />
        </AppSubpage>
      );
    },
  });
